import { useEffect, useMemo, useState } from 'react'
import { geoNaturalEarth1, geoPath } from 'd3-geo'
import type { Feature, FeatureCollection, Geometry } from 'geojson'

interface ClicsPorPais {
  nombre: string
  clics: number
}

interface WorldMapProps {
  /** Clics agregados por país (campo `pais` de los clics, obtenido por IP). */
  data: ClicsPorPais[]
  /** País actualmente filtrado (nombre tal cual lo devuelve el backend/ip-api). */
  selectedPais?: string | null
  /** Se invoca al hacer clic en un país con datos; recibe su nombre original. */
  onSelectPais?: (nombre: string) => void
}

interface PaisProps {
  shapeName: string
  iso_a2: string
}

type PaisFeature = Feature<Geometry, PaisProps>
type PaisesCollection = FeatureCollection<Geometry, PaisProps>

const GEOJSON_URL = '/geo/world-paises.geojson'
const VIEWBOX_W = 500
const VIEWBOX_H = 250

// Alias para alinear los nombres que devuelve ip-api.com con los `shapeName`
// del geojson (Natural Earth). Clave y valor se comparan ya normalizados.
const PAIS_ALIAS: Record<string, string> = {
  'united states': 'united states of america',
  'usa': 'united states of america',
  'democratic republic of the congo': 'dem. rep. congo',
  'republic of the congo': 'congo',
  'ivory coast': "côte d'ivoire",
  'czechia': 'czech republic',
  'czech republic': 'czechia',
  'south korea': 'south korea',
  'north korea': 'north korea',
  'bosnia and herzegovina': 'bosnia and herz.',
  'dominican republic': 'dominican rep.',
  'central african republic': 'central african rep.',
  'south sudan': 's. sudan',
  'equatorial guinea': 'eq. guinea',
  'eswatini': 'eswatini',
  'myanmar': 'myanmar',
}

function normalizeName(name: string): string {
  return name.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim()
}

function useGeoJSON(url: string) {
  const [data, setData] = useState<PaisesCollection | null>(null)
  useEffect(() => {
    let active = true
    fetch(url)
      .then((r) => (r.ok ? (r.json() as Promise<PaisesCollection>) : Promise.reject(r.status)))
      .then((j) => active && setData(j))
      .catch(() => active && setData(null))
    return () => {
      active = false
    }
  }, [url])
  return data
}

export function WorldMap({ data, selectedPais, onSelectPais }: WorldMapProps) {
  const geo = useGeoJSON(GEOJSON_URL)
  const [hovered, setHovered] = useState<string | null>(null)
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 })

  // Clics por clave de país del geojson (nombre normalizado + alias aplicado).
  const clicsByPais = useMemo(() => {
    const map: Record<string, number> = {}
    for (const item of data) {
      const key = PAIS_ALIAS[normalizeName(item.nombre)] ?? normalizeName(item.nombre)
      map[key] = (map[key] || 0) + item.clics
    }
    return map
  }, [data])

  // Clave del geojson → nombre original del país (el que entiende el backend).
  // Solo los países con datos quedan mapeados, y por tanto son clicables.
  const nombreByKey = useMemo(() => {
    const map: Record<string, string> = {}
    for (const item of data) {
      const key = PAIS_ALIAS[normalizeName(item.nombre)] ?? normalizeName(item.nombre)
      if (!(key in map)) map[key] = item.nombre
    }
    return map
  }, [data])

  const maxClics = useMemo(() => {
    const values = Object.values(clicsByPais)
    return values.length > 0 ? Math.max(...values) : 1
  }, [clicsByPais])

  const pathGen = useMemo(() => {
    if (!geo) return null
    const proj = geoNaturalEarth1().fitSize([VIEWBOX_W, VIEWBOX_H], geo)
    return geoPath(proj)
  }, [geo])

  function clicsFor(feature: PaisFeature): number {
    return clicsByPais[normalizeName(feature.properties.shapeName)] || 0
  }

  function getColor(clics: number): string {
    if (clics === 0) return '#a8a29e' // stone-400
    const intensity = Math.max(0.15, clics / maxClics)
    // Interpola de teal-100 a teal-600
    const r = Math.round(204 - intensity * (204 - 13))
    const g = Math.round(251 - intensity * (251 - 148))
    const b = Math.round(241 - intensity * (241 - 136))
    return `rgb(${r},${g},${b})`
  }

  if (!geo || !pathGen) {
    return (
      <div className="flex h-[250px] items-center justify-center text-sm text-muted-foreground">
        Cargando mapa...
      </div>
    )
  }

  const hoveredClics = hovered ? clicsByPais[normalizeName(hovered)] || 0 : 0

  return (
    <div className="relative">
      <svg
        viewBox={`0 0 ${VIEWBOX_W} ${VIEWBOX_H}`}
        className="w-full h-auto"
        onMouseLeave={() => setHovered(null)}
      >
        {geo.features.map((feature) => {
          const d = pathGen(feature)
          if (!d) return null
          const name = feature.properties.shapeName
          const nombre = nombreByKey[normalizeName(name)]
          const hasData = nombre !== undefined
          const isSelected = hasData && nombre === selectedPais
          const fill = isSelected
            ? '#0f766e' // teal-700 (país filtrado)
            : hovered === name
              ? '#0d9488' // teal-600 (hover)
              : getColor(clicsFor(feature))
          return (
            <path
              key={feature.properties.iso_a2 || name}
              d={d}
              fill={fill}
              stroke={isSelected ? '#134e4a' : '#ffffff'}
              strokeWidth={isSelected ? 1 : 0.4}
              className={`transition-colors duration-150 ${hasData && onSelectPais ? 'cursor-pointer' : 'cursor-default'}`}
              onClick={() => hasData && onSelectPais?.(nombre)}
              onMouseEnter={(e) => {
                setHovered(name)
                const rect = (e.target as SVGPathElement).ownerSVGElement?.getBoundingClientRect()
                if (rect) setTooltipPos({ x: e.clientX - rect.left, y: e.clientY - rect.top })
              }}
              onMouseMove={(e) => {
                const rect = (e.target as SVGPathElement).ownerSVGElement?.getBoundingClientRect()
                if (rect) setTooltipPos({ x: e.clientX - rect.left, y: e.clientY - rect.top })
              }}
            />
          )
        })}
      </svg>

      {hovered && (
        <div
          className="absolute pointer-events-none bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-lg z-10"
          style={{ left: tooltipPos.x + 10, top: tooltipPos.y - 40 }}
        >
          <p className="font-semibold">{hovered}</p>
          <p className="text-gray-300">{hoveredClics} {hoveredClics === 1 ? 'clic' : 'clics'}</p>
        </div>
      )}
    </div>
  )
}
